import type { EvaluationContext } from '@openfeature/core';
import {
  FeatureFlag,
  addonFlag,
  buildEvaluationContext,
} from '~~/shared/featureFlags';

export { FeatureFlag, addonFlag };

// Flag values that the server evaluated during SSR, read until the browser provider has its context.
export const SSR_FEATURE_FLAGS_STATE = 'feature-flags:ssr';

// The browser plugin and the SSR plugin both build their context here, so both send the same attributes.
export const sessionFlagContext = ({
  authStore,
  oidcStore,
  publicConfig,
  anonId,
  ipCountry,
}: {
  authStore: ReturnType<typeof useAuthStore>;
  oidcStore: ReturnType<typeof useOidcStore>;
  publicConfig: ReturnType<typeof useRuntimeConfig>['public'];
  anonId: string | undefined;
  ipCountry: string | undefined;
}): EvaluationContext => {
  const user = authStore.user;
  const parsedToken = oidcStore.parsedToken;
  // Keycloak address claim first, then the IP country, as in the API.
  const keycloakCountry = (
    parsedToken.address as { country?: string } | undefined
  )?.country;

  return buildEvaluationContext({
    environment: publicConfig.goFeatureFlag.environment,
    region: authStore.activeRegion ?? publicConfig.grpc.primaryRegion,
    anonId,
    country: keycloakCountry ?? ipCountry,
    user: user
      ? {
          id: user.id,
          email: user.email,
          emailVerified: user.emailVerified,
          username:
            user.username ??
            (parsedToken.preferred_username as string | undefined),
          groups: (parsedToken.groups as string[] | undefined) ?? [],
        }
      : undefined,
  });
};

export default function useFeatureFlag() {
  const {
    $openFeatureClient,
    $openFeatureFlagVersion,
    $openFeatureReady,
    $openFeatureContextApplied,
  } = useNuxtApp();
  const ssrFlags = useState<Partial<Record<string, boolean>>>(
    SSR_FEATURE_FLAGS_STATE,
    () => ({}),
  );

  // Resolves once the initial targeting context has been applied, so a
  // one-shot read of a flag (e.g. branching the checkout flow at mount)
  // reflects group/country targeting instead of the empty bootstrap context.
  // Falls back to an already-resolved promise when the provider plugin
  // bailed out (missing endpoint / init failure) — flags are fail-closed
  // false in that case, so there's nothing to wait for.
  const whenReady = (): Promise<void> =>
    ($openFeatureReady as Promise<void> | undefined) ?? Promise.resolve();

  // Returns a ComputedRef so callers re-render when the OpenFeature cache
  // changes (initial fetchAll finishing, websocket pushes, login). Reading
  // the value at call time would freeze the first synchronous evaluation —
  // typically `false` before fetchAll resolves — and gated UI would stay
  // hidden even after the provider is ready.
  const hasAccessToFeature = (flag: FeatureFlag): ComputedRef<boolean> =>
    computed(() => {
      void $openFeatureFlagVersion?.value;
      if (
        import.meta.server ||
        !$openFeatureClient ||
        !$openFeatureContextApplied?.value
      ) {
        return ssrFlags.value[flag] ?? false;
      }
      return $openFeatureClient.getBooleanValue(flag, false);
    });

  // isAddonAvailable gates non-list addon surfaces (deep links, promo/beta cards) behind the
  // Addon-<id> flag. The backend GetOrderInformation list + purchase guards are the authoritative
  // gate (TV-1774); this is defense-in-depth. An ABSENT flag => available (default true), so
  // un-flagged addons (e.g. Modspace) are unaffected. When the flag can't be evaluated
  // client-side (SSR, or the OpenFeature plugin bailed out) we return false so a standalone
  // surface isn't rendered on an unverified state — the backend list still decides what actually
  // shows/sells.
  const isAddonAvailable = (chargebeeItemId: string): ComputedRef<boolean> =>
    computed(() => {
      void $openFeatureFlagVersion?.value;
      if (import.meta.server || !$openFeatureClient) {
        return false;
      }
      return $openFeatureClient.getBooleanValue(
        addonFlag(chargebeeItemId),
        true,
      );
    });

  return { hasAccessToFeature, isAddonAvailable, whenReady };
}
